import { useState, useEffect } from 'react';

export function HeroSection({
  onCommand,
  onCTAClick,
}: Readonly<{
  onCommand: (command: string) => void;
  onCTAClick: (type: 'early-access' | 'get-started') => void;
}>) {
  const commands = [
    'help',
    'demo',
    'install',
    'features',
    'deploy',
    'docs',
    'about',
  ];

  const asciiLines = [
    '███╗   ███╗██╗███████╗████████╗ █████╗ ',
    '████╗ ████║██║██╔════╝╚══██╔══╝██╔══██╗',
    '██╔████╔██║██║█████╗     ██║   ███████║',
    '██║╚██╔╝██║██║██╔══╝     ██║   ██╔══██║',
    '██║ ╚═╝ ██║██║██║        ██║   ██║  ██║',
    '╚═╝     ╚═╝╚═╝╚═╝        ╚═╝   ╚═╝  ╚═╝',
  ];

  // ============================================================
  // TYPEWRITER EFFECT
  // ============================================================
  const [displayedLines, setDisplayedLines] = useState<string[]>([]);
  const [lineIndex, setLineIndex] = useState(0);

  useEffect(() => {
    if (lineIndex >= asciiLines.length) return;

    const timer = setTimeout(() => {
      setDisplayedLines((prev) => [...prev, asciiLines[lineIndex]]);
      setLineIndex((prev) => prev + 1);
    }, 100);

    return () => clearTimeout(timer);
  }, [lineIndex]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-8 text-center px-4">
      {/* Main Title */}
      <div className="space-y-4">
        <div className="font-mono text-[0.6rem] tracking-[1px] leading-none sm:text-xs md:text-sm lg:text-base bg-linear-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent animate-gradient whitespace-pre">
          {displayedLines.join('\n')}
          {displayedLines.length < asciiLines.length && (
            <span className="inline-block animate-pulse">▋</span>
          )}
        </div>

        <h1 className="text-xl md:text-2xl text-gray-300">
          Make It Fast Through Automation
        </h1>
      </div>

      {/* Tagline */}
      <div className="max-w-2xl space-y-4 text-gray-400">
        <p>
          Automate your daily workflow, from deployment to documentation, in
          just a few minutes. Focus on what matters and let MIFTA handle the
          repetitive stuff.
        </p>
        <p className="text-sm">
          Stop doing the same steps again and again. With MIFTA, you can build
          once and let automation run the rest.
        </p>
      </div>

      {/* CTA Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 pt-4">
        <button
          onClick={() => onCTAClick('get-started')}
          className="px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-black rounded-lg transition-all transform hover:scale-105"
        >
          Get Started
        </button>
        <button
          onClick={() => onCTAClick('early-access')}
          className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-all transform hover:scale-105 flex items-center gap-2 justify-center"
        >
          <span>✦</span> Early Access
        </button>
      </div>

      {/* Command suggestions */}
      <div className="pt-8 space-y-3 w-full max-w-xl">
        <p className="text-gray-500 text-xs">Try these commands:</p>
        <div className="flex flex-wrap gap-2 justify-center">
          {commands.map((cmd) => (
            <button
              key={cmd}
              onClick={() => onCommand(cmd.replace('--', ''))}
              className="px-3 py-1.5 bg-[#2a2a2a] hover:bg-[#3a3a3a] text-cyan-400 rounded border border-[#3a3a3a] cursor-pointer transition-colors text-xs"
            >
              {cmd}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
